import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function StreaksCalendar({ userId, streakDays = 0 }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [practiceDays, setPracticeDays] = useState([]);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/users/${userId}/practice-calendar`, { params: { year, month } })
      .then(r => setPracticeDays(r.data.days || []))
      .catch(() => setPracticeDays([]));
  }, [userId, year, month]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const practiceMap = {};
  practiceDays.forEach(d => { practiceMap[d.date] = d.seconds; });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const totalPracticeDays = practiceDays.length;

  // 5-day streak progress
  const daysIntoStreak = streakDays % 5;
  const daysUntilReward = daysIntoStreak === 0 && streakDays > 0 ? 0 : 5 - daysIntoStreak;

  return (
    <div className="tt-card p-4" data-testid="streaks-calendar">
      {/* 5-Day Streak Progress Bar */}
      <div className="mb-4 p-3 rounded-xl" style={{ background: "#FFF7ED", border: "1px solid #FDE68A" }}>
        <div className="flex items-center justify-between mb-2">
          <p className="font-heading text-xs font-bold" style={{ color: "#92400E" }}>Streak Reward Progress</p>
          <p className="text-[10px]" style={{ color: "#B45309" }}>
            {daysUntilReward === 0 ? "Reward earned!" : `${daysUntilReward} day${daysUntilReward !== 1 ? "s" : ""} to next reward`}
          </p>
        </div>
        <div className="flex items-center justify-between gap-1">
          {[1, 2, 3, 4, 5].map(day => {
            const filled = daysIntoStreak >= day || (daysIntoStreak === 0 && streakDays > 0);
            const isRewardDay = day === 5;
            return (
              <div key={day} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-full h-2 rounded-full ${filled ? "" : ""}`}
                  style={{ background: filled ? (isRewardDay ? "#D97706" : "#FB923C") : "#E5E7EB" }} />
                <span className="text-sm">
                  {isRewardDay ? "🎁" : (filled ? "🔥" : "·")}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] mt-1 text-center" style={{ color: "#A0896C" }}>
          Practice 5 days in a row to earn a treasure reward!
        </p>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1 rounded-lg hover:bg-amber-50" data-testid="cal-prev">
          <ChevronLeft className="w-5 h-5" style={{ color: "#7A5946" }} />
        </button>
        <p className="font-heading text-base font-bold" style={{ color: "#4A3224" }}>
          {MONTH_NAMES[month - 1]} {year}
        </p>
        <button onClick={nextMonth} className="p-1 rounded-lg hover:bg-amber-50" data-testid="cal-next">
          <ChevronRight className="w-5 h-5" style={{ color: "#7A5946" }} />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_NAMES.map(d => (
          <div key={d} className="text-center text-[9px] font-semibold" style={{ color: "#A0896C" }}>{d}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const practiced = practiceMap[dateStr];
          const isToday = dateStr === todayStr;

          return (
            <div key={dateStr}
              className={`relative w-full aspect-square rounded-lg flex items-center justify-center text-xs transition-all ${
                isToday ? "ring-2 ring-amber-400" : ""
              }`}
              style={{
                background: practiced ? "#FEF3C7" : "transparent",
                border: practiced ? "1px solid #FDE68A" : "1px solid transparent"
              }}
              data-testid={`cal-day-${day}`}>
              {practiced ? (
                <span className="text-lg leading-none" title={`${Math.round(practiced/60)} min`}>
                  🔥
                </span>
              ) : (
                <span style={{ color: isToday ? "#D97706" : "#B5A08C", fontWeight: isToday ? 700 : 400 }}>
                  {day}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-3 pt-2 flex items-center justify-between" style={{ borderTop: "1px dashed #D8C3B5" }}>
        <p className="text-xs" style={{ color: "#7A5946" }}>
          <span className="font-semibold">{totalPracticeDays}</span> days practiced this month
        </p>
        <p className="text-xs font-semibold" style={{ color: "#D97706" }}>
          🔥 {streakDays} day streak
        </p>
      </div>
    </div>
  );
}
